import { useRef, useState } from "react";
import { Avatar, IconImage } from "@veto/ui";
import type { ProfileMedia } from "@veto/protocol";
import { processProfileMediaFile, useProfileMediaUrl } from "../profile/profileMedia";

const ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,.jpg,.jpeg,.png,.webp,.gif,.mp4,.webm";

/** Round avatar picker — photo, GIF or short video loop, processed locally. */
export function AvatarPicker({
  name,
  value,
  onChange,
  size = 96,
  onError,
}: {
  name: string;
  value: ProfileMedia | null;
  onChange: (media: ProfileMedia | null) => void;
  size?: number;
  onError: (message: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const url = useProfileMediaUrl(value);

  function handlePick(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    void (async () => {
      try {
        onChange(await processProfileMediaFile(file));
      } catch (e) {
        onError(e instanceof Error ? e.message : "Could not use that file");
      } finally {
        setBusy(false);
        if (inputRef.current) inputRef.current.value = "";
      }
    })();
  }

  return (
    <div className="avatar-picker">
      <button
        type="button"
        className="avatar-picker-target"
        style={{ width: size, height: size }}
        aria-label={value ? "Change profile photo" : "Add profile photo"}
        title="Add photo, GIF or short video"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        <Avatar
          name={name || "V"}
          size={size}
          media={url && value ? { url, kind: value.kind } : null}
        />
        <span className="avatar-picker-badge" aria-hidden>
          {busy ? "…" : <IconImage size={14} />}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={(e) => handlePick(e.target.files?.[0])}
      />
    </div>
  );
}
